import { useAtomValue, useSetAtom, type Atom } from 'jotai';
import { Inspector, chromeDark } from 'react-inspector';

import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';

import { StoreAsTmpButton, getStoreAsTmpFn } from './store-as-tmp-button';

interface BunjaInstanceValueInspectorProps {
  value: unknown;
}
export const BunjaInstanceValueInspector = ({ value }: BunjaInstanceValueInspectorProps) => {
  if (!value || typeof value !== 'object') return <PlainValueInspector value={value} />;
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return <PlainValueInspector value={value} />;
  return (
    <div className={stack({ gap: 8, p: 8, rounded: 4 })} style={{ background: chromeDark.BASE_BACKGROUND_COLOR }}>
      {entries.map((entry) =>
        entry[0].endsWith('Atom') ? (
          <AtomBivEntry key={entry[0]} entry={entry} />
        ) : (
          <PlainBivEntry key={entry[0]} entry={entry} />
        ),
      )}
    </div>
  );
};

const PlainValueInspector = ({ value }: BunjaInstanceValueInspectorProps) => {
  return (
    <div className={css({ p: 8, rounded: 4 })} style={{ background: chromeDark.BASE_BACKGROUND_COLOR }}>
      <Inspector theme='chromeDark' table={false} data={value} />
    </div>
  );
};

interface BivEntryProps {
  entry: [string, unknown];
}

const AtomBivEntry = ({ entry }: BivEntryProps) => {
  const [key, atom] = entry as [string, Atom<unknown>];
  const readable = 'read' in atom;
  const writable = 'write' in atom;
  const readonly = !writable && readable;
  const writeonly = writable && !readable;
  // oxlint-disable-next-line
  const value = readable ? useAtomValue(atom) : useSetAtom(atom);
  return <PlainBivEntry entry={[`${key}${readonly ? ' (readonly)' : writeonly ? ' (writeonly)' : ''}`, value]} />;
};

const PlainBivEntry = ({ entry }: BivEntryProps) => {
  const [key, value] = entry;
  const storeAsTmp = getStoreAsTmpFn(key, value);
  return (
    <div key={key} className={stack({ gap: 4 })}>
      <div className={flex({ gap: 12, alignItems: 'center' })}>
        <span>{key}</span>
        <StoreAsTmpButton onClick={storeAsTmp} />
      </div>
      <div className={css({ ml: 8 })}>
        <Inspector theme='chromeDark' table={false} data={value} />
      </div>
    </div>
  );
};
